import gql from 'graphql-tag';
import {
  dvrDeviceListItemFields,
  dvrCameraItemFields,
  deviceUserFields,
} from './fragments';
import { paginationFragment } from '../shared/paginationFragment';

export const getDevice = gql`
  query device($deviceId: Int!) {
    device(deviceId: $deviceId) {
      id
      name
      placeId
      deviceTypeId
      parentDeviceId
      serialNumber
      ip
      isOnline
      updateStatus
      serializedParams
      creationDate
    }
  }
`;

export const deviceHomologatedFirmwares = gql`
  query deviceHomologatedFirmwares($deviceId: Int!) {
    deviceHomologatedFirmwares(deviceId: $deviceId) {
      version
      capabilities
      id
      creationDate
    }
  }
`;

export const deviceHomologatedModels = gql`
  query deviceHomologatedModels($deviceTypeId: Int!) {
    deviceHomologatedModels(deviceTypeId: $deviceTypeId) {
      deviceTypeId
      name
      fieldName
      capabilities
      id
      creationDate
    }
  }
`;

export const getRelayStatusCommand = gql`
  query relayStatusCommand($eventId: ID!, $deviceId: ID!) {
    relayStatusCommand(eventId: $eventId, deviceId: $deviceId) {
      pgms {
        number
        status
      }
    }
  }
`;

const divergenceTypesFields = gql`
  fragment DeviceInformationsDivergenceTypesFields on DeviceInformationsDivergenceTypes {
    person {
      entityId
      deviceUserId
      name
      linkId
      deviceQuantity
      expectedQuantity
    }
    guest {
      entityId
      deviceUserId
      name
      linkId
      deviceQuantity
      expectedQuantity
    }
    transientPerson {
      entityId
      deviceUserId
      name
      linkId
      deviceQuantity
      expectedQuantity
    }
    unknown {
      entityId
      deviceUserId
      name
      linkId
      deviceQuantity
      expectedQuantity
    }
    dweller {
      entityId
      deviceUserId
      name
      linkId
      deviceQuantity
      expectedQuantity
    }
    longTermGuest {
      entityId
      deviceUserId
      name
      linkId
      deviceQuantity
      expectedQuantity
    }
    temporaryAccessDweller {
      entityId
      deviceUserId
      name
      linkId
      deviceQuantity
      expectedQuantity
    }
  }
`;

export const getDeviceInformations = gql`
  ${divergenceTypesFields}
  query deviceInformations($deviceId: Int!) {
    deviceInformations(deviceId: $deviceId) {
      error
      messageError
      cards {
        cardKey
        cardName
        quantityDevice
        quantityMax
        quantityPorter
        divergence {
          shouldBeRegistered {
            ...DeviceInformationsDivergenceTypesFields
          }
          shouldNotBeRegistered {
            ...DeviceInformationsDivergenceTypesFields
          }
        }
      }
    }
  }
`;

export const getDeviceUsers = gql`
  ${deviceUserFields}
  ${paginationFragment}
  query deviceUsers(
    $deviceId: Int!
    $page: Int
    $pageSize: Int
    $search: String
  ) {
    deviceUsers(
      deviceId: $deviceId
      page: $page
      pageSize: $pageSize
      search: $search
    ) {
      collection {
        ...DeviceUserFields
      }
      pagination {
        ...paginationParts
      }
    }
  }
`;

export const getDeviceFormParams = gql`
  query deviceFormParams($deviceId: Int!) {
    deviceFormParams(deviceId: $deviceId) {
      warning
      messageWarning
      error
      messageError
      lastSyncDate
      lastSyncUser
      sections {
        sectionName
        sectionDescription
        isManaged
        values {
          name
          description
          manufacturerParamName
          deviceValue
          expectedValue
          referenceValue
          options {
            label
            value
            manufacturerLabel
          }
          constraints {
            min
            max
            step
          }
          referenceRange {
            min
            max
          }
          parameterId
          type
          unit
          warning
        }
      }
    }
  }
`;

export const availableGroupUnities = gql`
  query availableGroupUnities($condominiumId: ID!) {
    availableGroupUnities(condominiumId: $condominiumId) {
      groupUnities {
        id
        name
      }
    }
  }
`;

export const deviceUnitiesGroup = gql`
  query deviceUnitiesGroup($deviceId: ID!) {
    deviceUnitiesGroup(deviceId: $deviceId) {
      groupUnities {
        id
        name
      }
    }
  }
`;

export const deviceMultibuildingType = gql`
  query deviceMultibuildingType($deviceId: ID!) {
    deviceMultibuildingType(deviceId: $deviceId) {
      multibuildingType
    }
  }
`;

export const deviceTypesAvailableToCreate = gql`
  query deviceTypesAvailableToCreate {
    deviceTypesAvailableToCreate {
      deviceTypeAvailables {
        name
        types {
          id
          manufacturer
        }
      }
    }
  }
`;

export const listDvr = gql`
  ${dvrDeviceListItemFields}
  query listDvr($condominiumId: ID!) {
    listDvr(condominiumId: $condominiumId) {
      ...DvrDeviceListItemFields
    }
  }
`;

export const videoChannelDiagnostic = gql`
  query videoChannelDiagnostic($condominiumId: ID!, $cameraId: ID!) {
    videoChannelDiagnostic(condominiumId: $condominiumId, cameraId: $cameraId) {
      cameraId
      porterVideoChannel
      health {
        state
        code
        tracks
        params {
          readers
          megabytes
        }
        since
      }
      encoder {
        main {
          videoEnabled
          videoCodec
          smartCodec
          audioEnabled
          audioCodec
          bitrate
          bitrateControl
          resolution
          frameRate
        }
        substream {
          videoEnabled
          videoCodec
          smartCodec
          audioEnabled
          audioCodec
          bitrate
          bitrateControl
          resolution
          frameRate
        }
      }
      findings {
        code
      }
      unavailableReason
      readAt
    }
  }
`;

export const listDvrCameras = gql`
  ${dvrCameraItemFields}
  query listDvrCameras($condominiumId: ID!, $deviceId: ID!) {
    listDvrCameras(condominiumId: $condominiumId, deviceId: $deviceId) {
      ...DvrCameraItemFields
    }
  }
`;

export const listVideoDevicesInStock = gql`
  ${dvrDeviceListItemFields}
  query listVideoDevicesInStock($condominiumId: ID!) {
    listVideoDevicesInStock(condominiumId: $condominiumId) {
      ...DvrDeviceListItemFields
    }
  }
`;

export const listPorterVideoAllowedTypes = gql`
  query listPorterVideoAllowedTypes {
    listPorterVideoAllowedTypes
  }
`;

export const getDevicePassword = gql`
  query devicePassword($deviceId: ID!, $reason: String!) {
    devicePassword(deviceId: $deviceId, reason: $reason) {
      password
      user
    }
  }
`;

export const getCondominiumPasswordViewer = gql`
  query condominiumPasswordViewer($condominiumId: ID!) {
    condominiumPasswordViewer(condominiumId: $condominiumId) {
      personContextId
      personName
      lastViewDate
    }
  }
`;

export const getCondominiumPasswordActivated = gql`
  query condominiumPasswordActivated($condominiumId: ID!) {
    condominiumPasswordActivated(condominiumId: $condominiumId) {
      isManaged
      recoveryEmail
    }
  }
`;

export const availableDeviceFlags = gql`
  query availableDeviceFlags($deviceId: ID!, $placeId: ID!) {
    availableDeviceFlags(deviceId: $deviceId, placeId: $placeId) {
      checks {
        key
        label
        withLinkedController
        withoutLinkedController
      }
    }
  }
`;

export const deviceConnectionTestAvailability = gql`
  query deviceConnectionTestAvailability(
    $condominiumId: Int!
    $deviceId: Int
    $deviceTypeId: Int
  ) {
    deviceConnectionTestAvailability(
      condominiumId: $condominiumId
      deviceId: $deviceId
      deviceTypeId: $deviceTypeId
    )
  }
`;
